import { z } from "zod";
import { cleanHTML } from "./stringUtils";
import { Constant } from "./Constant";
import { TranslationFunction } from "./utils";

export const textValidation = (
  t: TranslationFunction,
  minLength?: number,
  maxLength?: number,
  optional?: boolean,
) =>
  optional
    ? z
      .string()
      .nullable()
      .optional()
      .refine((val) => val !== null && (val ?? "").length == 0, {
        message: t("validation.requiredMin", {
          count: minLength ?? Constant.formTextMinLength,
        }),
      })
    : z
      .string({
        required_error: t("validation.required"),
        invalid_type_error: t("validation.required"),
      })
      .refine(
        (value) =>
          cleanHTML(value).length >= (minLength ?? Constant.formTextMinLength),
        {
          message: t("validation.requiredMin", {
            count: minLength ?? Constant.formTextMinLength,
          }),
        },
      )
      .refine(
        (value) =>
          cleanHTML(value).length <= (maxLength ?? Constant.formTextMaxLength),
        {
          message: t("validation.requiredMax", {
            count: maxLength ?? Constant.formTextMaxLength,
          }),
        },
      );

export const textValidationOptional = () => z.string().nullable().optional();

export const urlValidation = (t: TranslationFunction) =>
  z
    .string()
    .refine(
      (value) =>
        value === "" ||
        /^https?:\/\/(www.)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+(:\d+)?(\/[^\s]*)?$/
          .test(
            value,
          ),
      t("validation.url"),
    )
    .nullable()
    .optional();

export const titleValidation = (t: TranslationFunction) =>
  z
    .string({
      required_error: t("validation.required"),
      invalid_type_error: t("validation.required"),
    })
    .nonempty({ message: t("validation.required") })
    .min(5, { message: t("validation.requiredMin", { count: 3 }) })
    .max(120, { message: t("validation.requiredMax", { count: 120 }) });

export const descriptionValidation = (t: TranslationFunction) =>
  z
    .string({
      required_error: t("validation.required"),
      invalid_type_error: t("validation.required"),
    })
    .refine((val) => val.length >= 5, {
      message: t("validation.requiredMin", { count: 5 }),
    })
    .refine((val) => !val.includes("\n"), {
      message: t("validation.noNewLines"),
    })
    .optional();

export const commentValidation = () => z.string().nullable().optional();

export const numberValidation = (t: TranslationFunction) =>
  z
    .union([z.string(), z.number()])
    .transform((val) => (typeof val === "string" ? parseInt(val, 10) : val)) // Convertit en nombre
    .refine((val) => Number.isInteger(val) && val >= 0, {
      message: t("validation.integer"),
    })
    .optional();

export const idValidation = () => z.union([z.string(), z.number()]);

export const idValidationOptional = () =>
  z.union([z.string(), z.number()]).nullable().optional();

export const imageValidation = () =>
  z
    .union([z.string(), z.instanceof(File)])
    .nullable()
    .optional();

export const listValidation = (t: TranslationFunction, minLength?: number) =>
  z
    .array(z.string().transform((val) => val.trim()))
    .transform((val) => val.filter((item) => item.length > 0))
    .refine(
      (arr) =>
        arr.filter((item) => item.length > 0).length >=
        (minLength ?? Constant.formListMinLength),
      {
        message: t("validation.arrayMin", {
          count: minLength ?? Constant.formListMinLength,
        }),
      },
    );
